<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';

import Store from '@store/dataProvider';
import { storeToRefs } from 'pinia';
import { IPagination } from '../../types/pagination.type';
import { useToast } from '../../composables/useToast';
import { useI18n } from 'vue-i18n';
import CompaniesGPSRepository from '@repositories/companiesGPS.repository';
import { ICompaniasGPS } from '../../types/companiasGPS.type';
import DataTable from '@components/DataTable.vue';

const isLoadingCompaniesGPS = ref<boolean>(false);

const { t } = useI18n();
const store = Store();
const { $toast } = useToast();
const { companiesGPS: companiesGPSPinia }: any = storeToRefs(store);
const companiesGPS = ref<any[]>([]);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const modalType = ref<string | null>(null);
const modalTitle = ref<string | undefined>();
const isModalOpen = ref<boolean>(false);
const selectedCompanyGPS = ref<any | null>(null);
const isViewActive = ref<boolean>(true);
const inputSearch = ref<string>('');
const searchFormRef = ref<any>(null);
const fieldsTableInactive: any = ref([
  {
    key: 'id',
    label: t('ajustes.companias_gps.id'),
    sortable: true,
  },
  {
    key: 'description',
    label: t('global.nombre'),
  },
  {
    key: 'email_1',
    label: t('ajustes.companias_gps.email1'),
  },
  {
    key: 'email_2',
    label: t('ajustes.companias_gps.email2'),
  },
  {
    key: 'email_3',
    label: t('ajustes.companias_gps.email3'),
  },
  {
    key: 'email_4',
    label: t('ajustes.companias_gps.email4'),
  },
  {
    key: 'slug',
    label: t('ajustes.companias_gps.etiqueta'),
  },
  {
    key: 'restore',
    label: t('global.restaurar'),
  },
]);
const fieldsTableActive: any = ref([
  {
    key: 'id',
    label: t('ajustes.companias_gps.id'),
    sortable: true,
  },
  {
    key: 'description',
    label: t('global.nombre'),
  },
  {
    key: 'email_1',
    label: t('ajustes.companias_gps.email1'),
  },
  {
    key: 'email_2',
    label: t('ajustes.companias_gps.email2'),
  },
  {
    key: 'email_3',
    label: t('ajustes.companias_gps.email3'),
  },
  {
    key: 'email_4',
    label: t('ajustes.companias_gps.email4'),
  },
  {
    key: 'slug',
    label: t('ajustes.companias_gps.etiqueta'),
  },
  {
    key: 'typeContainerInfo',
    label: t('global.editar'),
  },
  {
    key: 'delete',
    label: t('global.eliminar'),
  },
]);
const fields: any = ref([
  {
    key: 'search',
    title: t('global.buscar'),
    type: 'text',
    placeholder: t('ajustes.companias_gps.buscar_compania_gps'),
    value: null,
    cols: 3,
  },
]);

// Load CompaniesGPS ABM
const getCompaniesGPSFromAPI = async (
  page: number,
  deleted: boolean = false,
  search: string = '' 
) => {
  isLoadingCompaniesGPS.value = true;
  try {
    const { data } = await CompaniesGPSRepository.getCompaniesGPS({
      page,
      deleted,
	  search
    });
    if (data && Array.isArray(data?.companiesGPS)) {
      paginationData.value = data.pagination;
      return data.companiesGPS;
    }
    return [];
  } catch (error) {
    console.error('Error while fetching companiesGPS:', error);
    return [];
  } finally {
    isLoadingCompaniesGPS.value = false;
  }
};

const updateCompaniesGPS = (newCompaniesGPS: ICompaniasGPS[]) => {
  companiesGPS.value = newCompaniesGPS;
  store.setProp({ key: 'companiesGPS', value: newCompaniesGPS });
};

const loadCompaniesGPS = async () => {
  const newCompaniesGPS = await getCompaniesGPSFromAPI(1);
  updateCompaniesGPS(newCompaniesGPS);
};

onMounted(loadCompaniesGPS);

// Create inputs from modals
const fieldsInputTypeCompaniesGPS = computed(() => {
  return [
    {
      key: 'description',
      type: 'text',
      placeholder: t('global.nombre'),
      title: t('global.nombre'),
      state: null,
      cols: 12,
      value: selectedCompanyGPS.value?.description,
      validations: {
        rules: {
          required: true,
        },
        messages: {
          required: t('global.es_obligatorio'),
        },
      },
    },
    {
      key: 'email_1',
      type: 'email',
      placeholder: t('ajustes.companias_gps.email1'),
      title: t('ajustes.companias_gps.email1'),
      value: selectedCompanyGPS.value?.email_1,
      state: null,
      cols: 12,
    },
    {
      key: 'email_2',
      type: 'email',
      placeholder: t('ajustes.companias_gps.email2'),
      title: t('ajustes.companias_gps.email2'),
      value: selectedCompanyGPS.value?.email_2,
      state: null,
      cols: 12,
    },
    {
      key: 'email_3',
      type: 'email',
      placeholder: t('ajustes.companias_gps.email3'),
      title: t('ajustes.companias_gps.email3'),
      value: selectedCompanyGPS.value?.email_3,
      state: null,
      cols: 12,
    },
    {
      key: 'email_4',
      type: 'email',
      placeholder: t('ajustes.companias_gps.email4'),
      title: t('ajustes.companias_gps.email4'),
      value: selectedCompanyGPS.value?.email_4,
      state: null,
      cols: 12,
    },
    {
      key: 'slug',
      type: 'text',
      placeholder: t('ajustes.companias_gps.aniade_una_etiqueta'),
      title: t('ajustes.companias_gps.etiqueta'),
      value: selectedCompanyGPS.value?.slug,
      state: null,
      cols: 12,
    },
  ];
});

// UI
const onChangePage = async (page: number) => {
  const newCompaniesGPS = await getCompaniesGPSFromAPI(page);
  updateCompaniesGPS(newCompaniesGPS);
  currentPage.value = page;
};

const handleOpenModalCreate = () => {
  modalTitle.value = t('ajustes.companias_gps.agregar_compania_gps');
  isModalOpen.value = true;
  modalType.value = 'modalCreateCompanyGPS';
};

const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  selectedCompanyGPS.value = null;
};

const handleActive = async () => {
  const companiesGPS = await getCompaniesGPSFromAPI(1);
  updateCompaniesGPS(companiesGPS);
  isViewActive.value = true;
};

const handleInactive = async () => {
  const companiesGPSDeleted = await getCompaniesGPSFromAPI(1, true);
  updateCompaniesGPS(companiesGPSDeleted);
  isViewActive.value = false;
};

const handleOpenModalDialog = (item: ICompaniasGPS) => {
  modalTitle.value = isViewActive.value
    ? `${t('ajustes.companias_gps.borrar_la_compania_gps_con_el_id')}: ${item.id}`
    : `${t('ajustes.companias_gps.restaurar_la_compania_gps_con_el_id')}: ${item.id}`;
  modalType.value = 'modalDialog';
  selectedCompanyGPS.value = item;
  isModalOpen.value = true;
};

//CRUD
const handleCreateTypeContainer = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;
  try {
    const data = await CompaniesGPSRepository.createcompanyGPS(items);
    store.setProp({
      key: 'companiesGPS',
      value: [...companiesGPS.value, data],
    });
    $toast?.success(t('ajustes.companias_gps.compania_gps_agregada_con_exito'));
  } catch (error) {
    $toast?.error(t('global.ocurrio_un_error'));
  }

  handleClose();
};

const handleSelectTypeContainer = (typeCompanyGPSData: ICompaniasGPS) => {
  modalTitle.value = t('ajustes.companias_gps.actualizar_compania_gps');
  (isModalOpen.value = true), (modalType.value = 'modalUpdateCompanyGPS');
  selectedCompanyGPS.value = typeCompanyGPSData;
};

const handleUpdateTypeContainer = async ({ items, isFormValid }: any) => {
  if (!isFormValid || !selectedCompanyGPS.value) return;
  const typeContainerForUpdate = {
    ...items,
    id: selectedCompanyGPS.value.id,
  };

  try {
    const { data } = await CompaniesGPSRepository.updatecompanyGPS(
      typeContainerForUpdate,
    );

    let index = store.companiesGPS.findIndex(
      (value: any) => value.id === data.id,
    );

    if (index > -1) {
      store.setProp({ key: 'companiesGPS', index, value: data });
    }
    $toast?.success(
      t('ajustes.companias_gps.compania_gps_actualizado_con_exito'),
    );
  } catch (error) {
    $toast?.error(t('global.ocurrio_un_error'));
  }

  handleClose();
};

const handleDeleteRestoreTypeContainer = async () => {
  if (selectedCompanyGPS?.value?.id) {
    let response = null;
    try {
      if (isViewActive.value) {
        response = await CompaniesGPSRepository.deletecompanyGPS(
          selectedCompanyGPS.value.id,
        );
      } else {
        response = await CompaniesGPSRepository.restorecompanyGPS(
          selectedCompanyGPS.value.id,
        );
      }

      if (response === 200) {
        store.deleteItem({
          key: 'companiesGPS',
          value: selectedCompanyGPS?.value?.id,
        });
      }
    } catch (error) {
      if (isViewActive.value) {
        return $toast?.error(t('global.ocurrio_un_error'));
      }
      $toast?.error(t('global.ocurrio_un_error'));
    }

    handleClose();
  }
};
const handleOnSubmit = async ({ items }: any) => {
  inputSearch.value = items.search;
  const companiesGPS = await getCompaniesGPSFromAPI(1,false, items.search);
  updateCompaniesGPS(companiesGPS);
};
const handleReset = async () => {
  if (!searchFormRef.value) return;
  await searchFormRef.value.resetValues();
  inputSearch.value = '';
  const companiesGPS = await getCompaniesGPSFromAPI (1, false ,inputSearch.value);
  updateCompaniesGPS(companiesGPS);
};
</script>

<template>
  <Layout
    :title="t('ajustes.companias_gps.companias_gps')"
    :textColor="'color-secondary'"
  >
    <template #layout>
		<div>
			<Form
				ref="searchFormRef"
				@onSubmit="handleOnSubmit"
				:inputs="fields"
				customClass="poliza__form"
			>
			<template #buttons>
				<CButton
				class="background-blue button__form"
				style="padding: 0.6rem !important; margin: 0.6rem"
				>
				<template #content> {{ t('global.buscar') }} </template>
				</CButton>
				<CButton
				class="background-blue button__form"
				style="padding: 0.6rem !important; margin: 0.6rem 0px"
				type="button"
				@click="handleReset"
				>
				<template #content> {{ t('global.limpiar_filtro') }} </template>
				</CButton>
			</template>
			</Form>
		</div>
      <div class="d-flex justify-content-between">
        <div>
          <CButton
            class="background-blue button__form"
            style="padding: 0.5rem !important; margin-right: 15px"
            type="button"
            @click="handleActive"
          >
            <template #content>{{ t('global.activos') }}</template>
          </CButton>
          <CButton
            class="background-pink button__form"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleInactive"
          >
            <template #content>{{ t('global.inactivos') }}</template>
          </CButton>
        </div>
        <CButton
          class="background-pink button__form"
          style="padding: 0.5rem !important"
          type="button"
          @click="handleOpenModalCreate"
        >
          <template #content>
            {{ t('ajustes.companias_gps.agregar_compania_gps') }}
          </template>
        </CButton>
      </div>

      <DataTable
        :loading="isLoadingCompaniesGPS"
        :fields="isViewActive ? fieldsTableActive : fieldsTableInactive"
        :items="companiesGPSPinia"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(typeContainerInfo)="typeContainerData">
          <div
            @click="handleSelectTypeContainer(typeContainerData.data.item)"
            class="button-icon"
          >
            <img src="/icons/editar.svg" />
          </div>
        </template>
        <template v-slot:cell(delete)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon"
          >
            <img src="/icons/basura.svg" alt="eliminar" />
          </div>
        </template>
        <template v-slot:cell(restore)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon restore"
          >
            <img src="/icons/restaurar.svg" alt="restore" />
          </div>
        </template>
      </DataTable>

      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="modalTitle"
      >
        <template v-if="modalType === 'modalCreateCompanyGPS'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputTypeCompaniesGPS"
            @onSubmit="handleCreateTypeContainer"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>
                        {{
                          t('ajustes.companias_gps.agregar_compania_gps')
                        }}</span
                      >
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalUpdateCompanyGPS'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputTypeCompaniesGPS"
            @onSubmit="handleUpdateTypeContainer"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t('global.actualizar') }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalDialog'">
          <h5 class="text-center">
            {{
              isViewActive
                ? `${t('ajustes.companias_gps.esta_seguro_que_desea_eliminar_la_compania_gps')} ${selectedCompanyGPS?.description}?`
                : `${t('ajustes.companias_gps.esta_seguro_que_desea_restaurar_la_compania_gps')} ${selectedCompanyGPS?.description}?`
            }}
          </h5>
          <div class="d-flex mt-3">
            <CButton
              class="w-100 fs-6 p-2"
              background="background-pink"
              @click="handleDeleteRestoreTypeContainer"
            >
              <template #content>
                <div>
                  <span>{{ t('global.confirmar') }}</span>
                </div>
              </template>
            </CButton>
          </div>
        </template>
      </BModal>
    </template>
  </Layout>
</template>
